import React from 'react';
import {
  Accordion,
  AccordionElement,
  AccordionHeader,
  AccordionHeading,
  AccordionIcon,
  AccordionPanel,
  AccordionTitle,
  AccordionToggleButton,
  useAccordion,
} from '../../../_lib/components/Accordion';
import { VersionedSharedFileInfo } from './SharedFilesInformation';
import { MetaInfoFile } from '../../../_lib/components/MetaInfo';
import { Badge, BadgeModifier } from '../../../_lib/components/Badge';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { Heading } from '../../../_lib/components/Heading/Heading';
import { DateFormat, DateFormats } from '../../../_lib/components/DateFormat';

interface SharedFilesAccordionDefaultProps {
  versionInfo: VersionedSharedFileInfo[];
}

const mapToAccordionData = (info: VersionedSharedFileInfo[]) =>
  info.map((versionedFile, index) => {
    const { version, fileInfo } = versionedFile;
    return {
      id: version.toString(),
      category: fileInfo.fileTitle ? `${fileInfo.fileTitle} / ${fileInfo.fileName}` : fileInfo.fileName,
      currentVersion: index === 0,
      ...versionedFile,
    };
  });

export function SharedFilesAccordion({ versionInfo }: SharedFilesAccordionDefaultProps) {
  const { t } = useSonTranslation('SharedFiles');

  const data = mapToAccordionData(versionInfo);
  const { getAccordionProps, items } = useAccordion(data, {
    initialActiveElementId: data[0].id,
  });

  return (
    <>
      <Heading className="shared-files__info-heading" hierarchy={3}>
        {t('infoModal.versions')}
      </Heading>
      <Accordion {...getAccordionProps()}>
        {items.map((item) => {
          const { fileInfo, archiveDate, author, categories, changeComment, contentDate, description, keywords } = item;

          return (
            <AccordionElement {...item.getElementProps()} key={item.id}>
              <AccordionHeader>
                <AccordionHeading hierarchy={3}>
                  <AccordionToggleButton {...item.getToggleButtonProps()} modifier={['no-padding-left', 'small']}>
                    <span className="shared-files__inner-button">
                      <span className="shared-files__title-version">
                        <span className="shared-files__version">{item.version}</span>
                        <span className="shared-files__title">
                          <AccordionTitle category={item.category} />
                        </span>
                      </span>
                      {item.fileInfo.editDate && (
                        <span className="shared-files__date">
                          <DateFormat date={item.fileInfo.editDate} format={DateFormats.DATE_TIME} />
                        </span>
                      )}
                      {item.currentVersion && (
                        <span className="shared-files__badge">
                          <Badge modifier={[BadgeModifier.DARK, BadgeModifier.SMALL, BadgeModifier.SMALL_FONT]}>
                            {t('infoModal.currentVersion')}
                          </Badge>
                        </span>
                      )}
                    </span>
                    <AccordionIcon />
                  </AccordionToggleButton>
                </AccordionHeading>
              </AccordionHeader>
              <AccordionPanel {...item.getPanelProps()} modifier="no-border-top">
                <div className="shared-files__panel">
                  <MetaInfoFile
                    {...fileInfo}
                    archiveDate={archiveDate}
                    author={author}
                    changeComment={changeComment}
                    contentDate={contentDate}
                    description={description}
                    keywords={keywords}
                    categories={categories}
                  />
                </div>
              </AccordionPanel>
            </AccordionElement>
          );
        })}
      </Accordion>
    </>
  );
}
